import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Popover, PopoverBody, PopoverHeader, Progress, Row } from 'reactstrap'
import {
    canReorderColumn,
    canToggleColumnVisibility,
    columnConfigSignature,
    hasPickerControls,
    isColumnVisible,
    withPreferenceSettings,
} from './tableColumnPreferences'
import './TablePreferencePicker.css'

/**
 * The Customize Columns button and popover, with no opinion on where a saved view is kept.
 *
 * `onSave(draftColumnConfig)` resolves to `true` once the view is saved (the popover closes), to a
 * message to show in the popover, or to `false` to stay open without one. `buttonId` must be unique
 * on the page: the popover is anchored to it.
 */
function TableColumnPreferencePopover({
    buttonId = 'toggleTableColumnPickerButton',
    buttonLabel = 'PREFERENCE',
    placement = 'left',
    columnConfig = [],
    defaultColumns = [],
    onSave,
    busy = false,
    saving = false,
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [error, setError] = useState(null)
    const [draggedColumnId, setDraggedColumnId] = useState(null)
    const [dropIndicatorIndex, setDropIndicatorIndex] = useState(null)
    const [draftColumnConfig, setDraftColumnConfig] = useState(columnConfig || [])

    const defaultConfigSignature = useMemo(() => columnConfigSignature(defaultColumns), [defaultColumns])
    const draftConfigSignature = useMemo(() => columnConfigSignature(draftColumnConfig), [draftColumnConfig])

    useEffect(() => {
        if (!isOpen) {
            setDraftColumnConfig(columnConfig || [])
        }
    }, [columnConfig, isOpen])

    const openPopover = () => {
        setError(null)
        setDraftColumnConfig(columnConfig || [])
        setIsOpen(true)
    }

    const closePopover = () => {
        setError(null)
        setDraggedColumnId(null)
        setDropIndicatorIndex(null)
        setDraftColumnConfig(columnConfig || [])
        setIsOpen(false)
    }

    const togglePopover = () => {
        if (isOpen) {
            closePopover()
            return
        }
        openPopover()
    }

    const resetToDefault = () => {
        setError(null)
        setDraggedColumnId(null)
        setDropIndicatorIndex(null)
        setDraftColumnConfig(defaultColumns)
    }

    const toggleColumnVisibility = (columnId) => {
        setDraftColumnConfig(
            draftColumnConfig.map((column) => ({
                ...withPreferenceSettings(
                    column,
                    column.id === columnId
                        ? { isVisible: !isColumnVisible(column) }
                        : { isVisible: isColumnVisible(column) }
                ),
            }))
        )
    }

    const handleDragStart = (event, index) => {
        if (!canReorderColumn(draftColumnConfig[index])) return
        event.dataTransfer.effectAllowed = 'move'
        setDraggedColumnId(draftColumnConfig[index]?.id || null)
        setDropIndicatorIndex(index)
    }

    const handleDragOver = (event, hoverIndex) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'

        if (!draggedColumnId) {
            setDropIndicatorIndex(hoverIndex)
            return
        }

        const dragIndex = draftColumnConfig.findIndex((column) => column.id === draggedColumnId)
        if (dragIndex === -1 || dragIndex === hoverIndex) {
            setDropIndicatorIndex(hoverIndex)
            return
        }
        if (!canReorderColumn(draftColumnConfig[dragIndex]) || !canReorderColumn(draftColumnConfig[hoverIndex])) return

        const updatedColumns = [...draftColumnConfig]
        const [draggedColumn] = updatedColumns.splice(dragIndex, 1)
        updatedColumns.splice(hoverIndex, 0, draggedColumn)

        setDraftColumnConfig(
            updatedColumns.map((column, index) => ({
                ...column,
                order: index,
            }))
        )
        setDropIndicatorIndex(hoverIndex)
    }

    const handleDrop = (event, dropIndex) => {
        event.preventDefault()
        setDropIndicatorIndex(dropIndex)
        setDraggedColumnId(null)
    }

    const handleDragEnd = () => {
        setDraggedColumnId(null)
        setDropIndicatorIndex(null)
    }

    const saveView = async () => {
        setError(null)
        const outcome = await onSave(draftColumnConfig)
        if (outcome === true) {
            closePopover()
            return
        }
        if (typeof outcome === 'string') setError(outcome)
    }

    return (
        <>
            <a id={buttonId} className="driver-list-button" onClick={togglePopover}>
                {buttonLabel} | <i className="fa fa-table" aria-hidden="true"></i>
            </a>
            <Popover trigger="legacy" placement={placement} isOpen={isOpen} target={buttonId} toggle={togglePopover} className="table-preferences-popover">
                <PopoverHeader>
                    <div className="table-preferences-header">
                        <div>
                            <div className="table-preferences-title">Customize Columns</div>
                            <div className="table-preferences-subtitle">Drag to reorder, toggle to show or hide.</div>
                        </div>
                    </div>
                </PopoverHeader>
                <PopoverBody style={{ maxHeight: '360px', overflowY: 'auto' }}>
                    {error && (
                        <Row>
                            <Col><Alert color="danger">{error}</Alert></Col>
                        </Row>
                    )}
                    <div className="table-preferences-list-head">
                        <span>Columns</span>
                        <span>Visible</span>
                    </div>
                    <div className="table-preferences-list">
                        {draftColumnConfig.map((column, index) => (
                            <div
                                key={column.id || index}
                                className={[
                                    'table-preferences-item',
                                    !hasPickerControls(column) ? 'table-preferences-item-disabled' : '',
                                    draggedColumnId === column.id ? 'table-preferences-item-dragging' : '',
                                    dropIndicatorIndex === index ? 'table-preferences-item-drop-target' : '',
                                ].filter(Boolean).join(' ')}
                                draggable={canReorderColumn(column)}
                                onDragStart={(event) => handleDragStart(event, index)}
                                onDragOver={(event) => handleDragOver(event, index)}
                                onDrop={(event) => handleDrop(event, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <div className="table-preferences-item-main">
                                    <span className="drag-btn">
                                        <i className="fa fa-bars" aria-hidden="true"></i>
                                    </span>
                                    <div className="table-preferences-item-copy">
                                        <div className="table-preferences-item-name">{column.name}</div>
                                        <div className="table-preferences-item-meta">
                                            {!canToggleColumnVisibility(column) && !canReorderColumn(column)
                                                ? ''
                                                : canReorderColumn(column) && !canToggleColumnVisibility(column)
                                                    ? 'Can change order'
                                                    : !canReorderColumn(column) && canToggleColumnVisibility(column)
                                                        ? 'Can change visibility' : 'Can change visibility & order'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <label className="table-preferences-toggle">
                                    <input type="checkbox" aria-label={column.name} checked={isColumnVisible(column)} disabled={!canToggleColumnVisibility(column)} onChange={() => toggleColumnVisibility(column.id)} />
                                    <span className="table-preferences-toggle-slider"></span>
                                </label>
                            </div>
                        ))}
                    </div>
                    {busy && (
                        <Row>
                            <Col xs={12}><Progress animated color="info" value="100" /></Col>
                        </Row>
                    )}
                    <div className="table-preferences-actions">
                        <Button
                            className="reset-view-btn"
                            onClick={resetToDefault}
                            disabled={busy || draftConfigSignature === defaultConfigSignature}
                        >
                            Reset to Default
                        </Button>
                        <Button className="cancel-view-btn" onClick={closePopover} disabled={saving}>
                            Cancel
                        </Button>
                        <Button className="save-view-btn" onClick={saveView} disabled={busy}>
                            Save View
                        </Button>
                    </div>
                </PopoverBody>
            </Popover>
        </>
    )
}

export default TableColumnPreferencePopover
